'use client';

import { memo, useState, useMemo, useCallback } from 'react';
import { Button } from '@/components/common/Button';
import { Alert } from '@/components/common/Alert';
import { useSocket } from '@/hooks/useSocket';
import { SOCKET_EVENTS } from '@/lib/constants/socket';
import { PoolGrid } from '@/components/pool/PoolGrid';
import { logger } from '@/lib/monitoring/logger';
import type { Pool } from '@/lib/types/auction';

interface PoolSelectorProps {
  tournamentId: string;
  pools: Pool[];
  onPoolSelected?: () => void;
}

export const PoolSelector = memo(function PoolSelector({
  tournamentId,
  pools,
  onPoolSelected,
}: PoolSelectorProps) {
  const [selectedPool, setSelectedPool] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const { socket } = useSocket();

  /** ✅ Find selected pool */
  const selectedPoolData = useMemo(
    () => pools.find((pool) => pool.id === selectedPool),
    [pools, selectedPool]
  );

  /** ✅ Filter available players */
  const availablePlayers = useMemo(
    () =>
      selectedPoolData?.players?.filter(
        (player) => player.status === 'available' || player.status === 'unsold'
      ) ?? [],
    [selectedPoolData]
  );

  /** ✅ Handle Pool Selection */
  const handlePoolSelect = useCallback((pool: Pool) => {
    setSelectedPool(pool.id);
    setError(null); // Reset error when a new pool is selected
  }, []);

  /** ✅ Handle Start Auction */
  const handleStartAuction = useCallback(() => {
    if (!selectedPool || !socket) return;

    if (availablePlayers.length === 0) {
      setError('No available players in this pool');
      return;
    }

    try {
      socket.emit(SOCKET_EVENTS.START_AUCTION, {
        poolId: selectedPool,
        tournamentId,
        timeout: 300, // Default timeout: 5 minutes
        // availablePlayerIds: availablePlayers.map((p) => p.id),
      });

      logger.info('Auction started', { poolId: selectedPool });
      onPoolSelected?.();
    } catch (err) {
      console.error('❌ Auction Start Failed:', err);
      setError('Failed to start auction. Please try again.');
    }
  }, [selectedPool, socket, tournamentId, availablePlayers, onPoolSelected]);

  return (
    <div>
      {/* ✅ Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Select Pool for Auction
          </h3>
          {selectedPoolData && (
            <p className="mt-1 text-sm text-gray-500">
              {availablePlayers.length} players available for auction
            </p>
          )}
        </div>
        <Button
          onClick={handleStartAuction}
          disabled={!selectedPool || availablePlayers.length === 0}
          className="ml-4"
        >
          Start Pool Auction
        </Button>
      </div>

      {/* ✅ Pool Grid */}
      <PoolGrid
        pools={pools}
        onSelectPool={handlePoolSelect}
        selectedPoolId={selectedPool}
        emptyMessage="No available pools for auction"
        // actionButtonLabel="Start Auction"
        // onAction={handleStartAuction}
      />

      {/* ✅ Error Message */}
      {error && <Alert type="error" message={error} className="mt-4" />}
    </div>
  );
});
